import { ManageAssetWithLicense } from "../../../../common/api/decimal/common-types/manageAssets";
import { distance } from "fastest-levenshtein";

const VBID_LENGTH_DELTA = 0;
const START_WORD = 0.2;
const START_TEXT = 0.5;

export enum MatchType {
  Title = "title",
  Author = "author",
}

export interface Match {
  asset: ManageAssetWithLicense;
  matchType: MatchType;
}

export function getScoreThreshold(query: string): number {
  if (query.length <= 5) {
    return 1;
  }
  return 2;
}

export function computeFixedDistance(content: string, query: string): number {
  const index = content.indexOf(query);

  if (index === -1) {
    return Number.MAX_VALUE;
  }

  if (index === 0) {
    return -START_TEXT;
  }

  if (content.charAt(index - 1) === " ") {
    return -START_WORD;
  }

  return 0;
}

export function computeFuzzyDistance(content: string, query: string): number {
  if (query.length > content.length) {
    return Number.MAX_VALUE;
  }

  let score = Number.MAX_VALUE;
  for (let i = 0; i < content.length - query.length + 1; i++) {
    let adjustedScore = distance(content.substring(i, i + query.length), query);

    if (i === 0) {
      adjustedScore -= START_TEXT;
    } else if (content.charAt(i - 1) === " ") {
      adjustedScore -= START_WORD;
    }

    score = Math.min(score, adjustedScore);
    if (score < 0) {
      break;
    }
  }

  return score;
}

export function quickSearch(licenses: ManageAssetWithLicense[], query: string) {
  if (!query || query.length < 3) {
    return [];
  }

  const greatResults: Match[] = [];
  const okResults: Match[] = [];
  const scoreThreshold = getScoreThreshold(query);
  query = query.toLowerCase();

  licenses.forEach((license) => {
    const vbid = license.manageAsset.vbid;
    let score = 0;
    const titleScore = computeFuzzyDistance(
      license.manageAsset.title.toLowerCase(),
      query
    );
    const author = license.manageAsset.author
      ? license.manageAsset.author.toLowerCase()
      : "";
    const authorScore = computeFixedDistance(author, query);
    score = Math.min(titleScore, authorScore);

    if (
      score > 0 &&
      vbid.length - query.length <= VBID_LENGTH_DELTA &&
      vbid.substring(0, query.length + 1).toLowerCase() === query
    ) {
      score = Math.min(score, -1);
    }

    const result = {
      asset: license,
      matchType: titleScore > authorScore ? MatchType.Author : MatchType.Title,
    };

    if (score <= scoreThreshold) {
      if (score < 0) {
        greatResults.push(result);
      } else {
        okResults.push(result);
      }
    }
  });

  const results =
    greatResults.length >= 3 ? greatResults : greatResults.concat(okResults);

  return results.slice(0, 5);
}
